import { AppointmentBookStatus, Insurance, Priority } from '@/models/appointment/Appointment.ts';
import type { AppointmentBookingFormValues } from '@/models/appointment/AppointmentBookingFormValues.ts';
import type { EnrichedPractitionerData } from '@/models/appointment/EnrichedPractitionerData.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';

import type { PreBookedCollectionAppointment } from '@/schemas/chain-booking.schema.ts';

import type { CollectionBookingSummary, EnrichedAppointmentWithBookingStatus } from '@/stores/bookingStore.ts';

import { formToAppointment } from '@/utils/appointmentUtils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';

/** A booked collection is either a Terminserie (series) or a Terminkette (chain). */
export type CollectionKind = 'series' | 'chain';

export const pluralizeTermine = (count: number): string => `${count} ${count === 1 ? 'Termin' : 'Termine'}`;

export const formatCollectionLabel = (type: CollectionKind, count: number): string =>
    `${type === 'series' ? 'Terminserie' : 'Terminkette'} · ${pluralizeTermine(count)}`;

/** Single `DD.MM.YY` when all on one day, else `DD.MM.YY – DD.MM.YY` (earliest–latest); '' when empty. */
export const formatCollectionDateRange = (dateTimes: string[]): string => {
    const sorted = dateTimes.filter(Boolean).sort((a, b) => a.localeCompare(b));
    const first = sorted[0];
    const last = sorted.at(-1);
    if (!first || !last) return '';
    // Compare formatted day labels, not raw datetimes, so same-day appointments at different times collapse to one date.
    const firstLabel = dayjs(first).format('DD.MM.YY');
    const lastLabel = dayjs(last).format('DD.MM.YY');
    return firstLabel === lastLabel ? firstLabel : `${firstLabel} – ${lastLabel}`;
};

/** One-line series summary; undefined for a chain or when the form can't resolve. */
export const buildSeriesDetailsLabel = (
    mode: CollectionKind,
    appointments: EnrichedAppointmentWithBookingStatus[],
    formOptionData: EnrichedPractitionerData | undefined
): string | undefined => {
    if (mode !== 'series' || !appointments[0] || !formOptionData) return undefined;
    const data = formToAppointment(appointments[0].form_data, formOptionData);
    if (!data) return undefined;
    return `(${data.insurance}) ${data.type.name} - ${data.duration} min mit ${data.practitioner.name}`;
};

export type OrderedBookedItem =
    | { kind: 'collection'; order: number; collection: CollectionBookingSummary }
    | { kind: 'single'; order: number; appointment: EnrichedAppointmentWithBookingStatus };

/** Merges booked collections + single appointments into one list sorted by booking_order. */
export const mergeBookedItemsByOrder = (
    collectionBookings: CollectionBookingSummary[],
    singleAppointments: EnrichedAppointmentWithBookingStatus[] = []
): OrderedBookedItem[] =>
    [
        ...collectionBookings.map(
            (collection): OrderedBookedItem => ({
                kind: 'collection',
                order: collection.booking_order,
                collection: collection,
            })
        ),
        ...singleAppointments.map(
            (appointment): OrderedBookedItem => ({
                kind: 'single',
                order: appointment.booking_order ?? 0,
                appointment: appointment,
            })
        ),
    ].sort((first, second) => first.order - second.order);

/** Entries for a booked Series: all share currentForm, only date_time differs per appointment. */
export const buildSeriesSummaryEntries = (
    currentForm: AppointmentBookingFormValues,
    appointments: PreBookedCollectionAppointment[]
): EnrichedAppointmentWithBookingStatus[] =>
    appointments.map((appointment) => ({
        form_id: appointment.id,
        status: AppointmentBookStatus.BOOKED,
        form_data: { ...currentForm, date_time: appointment.start },
    }));

export interface ChainStepFormSource {
    aptType: { id: string; duration: { value: number } };
    calendarId: string;
    practice: { pvs?: { id?: string } };
}

interface BuildChainSummaryEntriesParams {
    appointments: PreBookedCollectionAppointment[];
    resolvedSteps: ChainStepFormSource[];
    insurance: Insurance;
    pvsProvider: PVSProvider;
}

/** Entries for a booked Chain; the non-display fields (description/info/offered/priority) are placeholders. */
export const buildChainSummaryEntries = ({
    appointments,
    resolvedSteps,
    insurance,
    pvsProvider,
}: BuildChainSummaryEntriesParams): EnrichedAppointmentWithBookingStatus[] =>
    appointments.flatMap((appointment, index) => {
        const step = resolvedSteps[index];
        if (!step) return [];
        return [
            {
                form_id: appointment.id,
                status: AppointmentBookStatus.BOOKED,
                form_data: {
                    pvs_provider: pvsProvider,
                    apt_type_id_or_schedule_type_pvs_id: step.aptType.id,
                    calendar_pvs_id: step.calendarId,
                    date_time: appointment.start,
                    duration: step.aptType.duration.value,
                    insurance: insurance,
                    description: '',
                    info: '',
                    is_offered: true,
                    practice_doc_cirrus_id: step.practice.pvs?.id ?? '',
                    priority: Priority.NORMAL,
                },
            },
        ];
    });
